import { TrendingUp, XCircle, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

function formatEur(n) {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency', currency: 'EUR', minimumFractionDigits: 0,
  }).format(Number(n ?? 0));
}

const CONFIDENCE_CLS = {
  high:   'text-success',
  medium: 'text-warning',
  low:    'text-destructive',
};

/**
 * ProposeSalaryPreview — Bedrock-powered salary increase proposal card.
 * Shows components breakdown + impact + reasoning before user confirms.
 */
export default function ProposeSalaryPreview({ action, onApprove, onReject, status }) {
  const { preview } = action;

  if (status === 'approved') {
    return (
      <div className="flex items-center gap-2 p-2 bg-success/10 border border-success/30 rounded-lg text-xs text-success my-1">
        <TrendingUp className="w-3.5 h-3.5" />
        <span>Subida aprobada — aplicando…</span>
      </div>
    );
  }
  if (status === 'rejected') {
    return (
      <div className="flex items-center gap-2 p-2 bg-muted border border-border rounded-lg text-xs text-muted-foreground my-1">
        <XCircle className="w-3.5 h-3.5" />
        <span>Propuesta descartada</span>
      </div>
    );
  }

  if (!preview) return null;
  const noChange = (preview.incremento_pct ?? 0) === 0;

  return (
    <div className="rounded-xl border border-indigo-200 dark:border-indigo-800 bg-indigo-50/60 dark:bg-indigo-950/20 p-4 space-y-3 my-2 max-w-sm">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <Badge variant="outline" className="text-indigo-700 dark:text-indigo-300 border-indigo-300 dark:border-indigo-700 text-[10px] mb-1">
            <Sparkles className="w-3 h-3 mr-0.5 inline" />Propuesta ARIA
          </Badge>
          <p className="text-sm font-semibold text-foreground leading-tight truncate">
            {preview.empleado}
          </p>
          <p className="text-[10px] text-muted-foreground">
            {formatEur(preview.salario_actual)} actual
          </p>
        </div>
        {!noChange && (
          <div className="text-right shrink-0">
            <p className="text-lg font-bold text-indigo-700 dark:text-indigo-300 tabular-nums">
              {formatEur(preview.salario_propuesto)}
            </p>
            <p className="text-[10px] font-semibold text-success">
              +{preview.incremento_pct?.toFixed(1)}%
            </p>
          </div>
        )}
      </div>

      {!noChange && preview.components && preview.components.length > 0 && (
        <div className="text-[11px] space-y-1 border-t border-indigo-200 dark:border-indigo-800 pt-2">
          {preview.components.map((c, i) => (
            <div key={i} className="flex justify-between">
              <span className="text-muted-foreground">{c.factor}</span>
              <span className="font-medium tabular-nums">{c.contribution_pct > 0 ? '+' : ''}{c.contribution_pct.toFixed(1)} pp</span>
            </div>
          ))}
        </div>
      )}

      <p className="text-xs text-foreground leading-snug italic">
        "{preview.reasoning}"
      </p>

      {!noChange && (
        <div className="text-[11px] grid grid-cols-2 gap-2 border-t border-indigo-200 dark:border-indigo-800 pt-2">
          <div>
            <p className="text-muted-foreground">Impacto anual</p>
            <p className="font-semibold tabular-nums">{formatEur(preview.diff_anual)}</p>
          </div>
          <div>
            <p className="text-muted-foreground">Coste total c/ SS</p>
            <p className="font-semibold tabular-nums">{formatEur(preview.coste_total_anual)}</p>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between text-[10px]">
        <span className={`font-medium ${CONFIDENCE_CLS[preview.confidence] ?? CONFIDENCE_CLS.medium}`}>
          Confianza: {preview.confidence ?? 'media'}
        </span>
      </div>

      {!noChange ? (
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={onReject} className="flex-1 h-7 text-xs">
            <XCircle className="w-3 h-3 mr-1" />Descartar
          </Button>
          <Button size="sm" onClick={onApprove} className="flex-1 h-7 text-xs bg-indigo-600 hover:bg-indigo-700 text-white">
            <TrendingUp className="w-3 h-3 mr-1" />Aplicar subida
          </Button>
        </div>
      ) : (
        <Button size="sm" variant="outline" onClick={onReject} className="w-full h-7 text-xs">
          Entendido
        </Button>
      )}
    </div>
  );
}
